import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Checkbox,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { login, type SessionState } from '@/shared/api/auth';
import { ApiError, ERROR_CODES } from '@/shared/api/http';
import { isMfaChallenge, type MfaChallenge } from '@/shared/api/mfa';
import { forgetSession, selectableMemberships } from '@/shared/session/session';

import { AuthLayout } from './AuthLayout';
import { loginSchema, type LoginFormValues } from './loginSchema';
import { MfaLoginStep } from './MfaLoginStep';
import { internalRedirect } from './redirectTarget';

/** `/login` (13 §2.5): one membership enters directly; several go through `/login/tenant`. */
export function LoginPage({ redirectTo }: { redirectTo?: string | undefined }) {
  const { t } = useTranslation('auth');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const enter = async (state: SessionState) => {
    // The login answer carries no permissions; the guard reloads the session with them.
    forgetSession(queryClient);
    const target = internalRedirect(redirectTo);
    if (state.activeTenant === null) {
      // `AM-134` point 11: a Platform Administrator with no membership at all - never a user of
      // any tenant (`DevelopmentSeeder`) - has nowhere to select, so it goes straight to its
      // own area instead of the tenant selector's "no access" message.
      if (state.identity.isPlatformAdmin && selectableMemberships(state).length === 0) {
        await navigate({ to: '/platform' });
      } else {
        // The selector takes the page the person was headed to along, and goes there once inside.
        await navigate({
          to: '/login/tenant',
          search: target === undefined ? {} : { redirect: target },
        });
      }
    } else if (target !== undefined) {
      router.history.push(target);
    } else {
      await navigate({ to: '/' });
    }
  };

  const submit = form.handleSubmit(async (values) => {
    setErrorKey(null);
    try {
      const answer = await login(values);
      if (isMfaChallenge(answer)) {
        setChallenge(answer);
        return;
      }
      await enter(answer);
    } catch (error) {
      setErrorKey(loginErrorKey(error));
    }
  });

  if (challenge !== null) {
    return (
      <AuthLayout>
        <Stack gap="sm" w="100%" maw={400}>
          <Card withBorder radius="md" padding={32}>
            <MfaLoginStep
              challenge={challenge}
              onSession={(state) => void enter(state)}
              onRestart={() => {
                setChallenge(null);
                form.resetField('password');
              }}
            />
          </Card>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack gap="sm" w="100%" maw={400}>
        <Card withBorder radius="md" padding={32}>
          <form onSubmit={(event) => void submit(event)} noValidate>
            <Stack gap="md">
              <Title order={2}>{t('login.title')}</Title>

              {errorKey !== null ? (
                <Alert color="red" variant="light" role="alert">
                  {t(errorKey)}
                </Alert>
              ) : null}

              <TextInput
                label={t('login.email')}
                type="email"
                autoComplete="username"
                size="md"
                error={translateError(t, form.formState.errors.email?.message)}
                {...form.register('email')}
              />

              <PasswordInput
                label={t('login.password')}
                autoComplete="current-password"
                size="md"
                error={translateError(t, form.formState.errors.password?.message)}
                {...form.register('password')}
              />

              <Group justify="space-between" align="center">
                <Controller
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <Checkbox
                      label={t('login.rememberMe')}
                      checked={field.value}
                      onChange={(event) => field.onChange(event.currentTarget.checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  )}
                />
                <Anchor component={Link} to="/forgot-password" size="sm">
                  {t('login.forgot')}
                </Anchor>
              </Group>

              <Button type="submit" size="md" fullWidth loading={form.formState.isSubmitting}>
                {t('login.submit')}
              </Button>
            </Stack>
          </form>
        </Card>
        <Text size="xs" c="dimmed" ta="center">
          {t('login.notice')}
        </Text>
      </Stack>
    </AuthLayout>
  );
}

function loginErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.errorCode === ERROR_CODES.invalidCredentials || error.status === 401) {
      return 'login.error.invalidCredentials';
    }
    if (error.errorCode === ERROR_CODES.rateLimitExceeded) {
      return 'login.error.rateLimited';
    }
  }

  return 'login.error.generic';
}

function translateError(t: (key: string) => string, key: string | undefined): string | undefined {
  return key === undefined ? undefined : t(key);
}
